import { isNewOrderSecurity } from '@src/components/OMS/NewOrder/utils';
import { EMPTY_ARRAY, useSecuritiesContext, type Security } from '@talos/kyoko';
import { createContext, memo, useContext, useMemo, type PropsWithChildren } from 'react';

type AvaOMSSearchableSecuritiesContextValue = {
  avaOMSSearchableSecurities: Security[];
};

const AvaOMSSearchableSecuritiesContext = createContext<AvaOMSSearchableSecuritiesContextValue | undefined>(undefined);
AvaOMSSearchableSecuritiesContext.displayName = 'AvaOMSSearchableSecuritiesContext';

export function useAvaOMSSearchableSecurities(): Security[] {
  const context = useContext(AvaOMSSearchableSecuritiesContext);
  if (context === undefined) {
    throw new Error('Missing AvaOMSSearchableSecuritiesProvider further up in the tree. Did you forget to add it?');
  }
  return context.avaOMSSearchableSecurities;
}

/**
 * Ava OMS allow-list over searchableSecurities (Share / GovernmentBond / OTC / STS digital excluded).
 * Kept out of Kyoko SecuritiesProvider so other SymbolSelectors still see those types.
 */
export const AvaOMSSearchableSecuritiesProvider = memo(function AvaOMSSearchableSecuritiesProvider({
  children,
}: PropsWithChildren) {
  const { searchableSecurities } = useSecuritiesContext();
  const avaOMSSearchableSecurities = useMemo(
    () => (searchableSecurities ?? EMPTY_ARRAY).filter(isNewOrderSecurity),
    [searchableSecurities]
  );
  const value = useMemo(
    (): AvaOMSSearchableSecuritiesContextValue => ({ avaOMSSearchableSecurities }),
    [avaOMSSearchableSecurities]
  );

  return (
    <AvaOMSSearchableSecuritiesContext.Provider value={value}>{children}</AvaOMSSearchableSecuritiesContext.Provider>
  );
});
